import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="border-b border-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/settings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white transition-colors mb-4">
            <ArrowLeft size={16} />
            Back to Settings
          </Link>
          <div className="flex items-center gap-3">
            <AlertTriangle size={28} className="text-gold" />
            <h1 className="font-serif text-3xl text-white">Disclaimer</h1>
          </div>
          <p className="text-muted-foreground mt-2">Last updated: March 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-invert prose-gold max-w-none space-y-8">
          
          {/* Important Notice Banner */}
          <div className="p-6 bg-gold/10 border border-gold/30 rounded-lg">
            <div className="flex items-start gap-4">
              <AlertTriangle size={24} className="text-gold shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-medium mb-2">Important Notice</h3>
                <p className="text-gray-300 text-sm">
                  The information provided on Assagart is for general informational and educational purposes only. 
                  It should not be construed as professional financial, investment, legal, or tax advice.
                </p>
              </div>
            </div>
          </div>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">1. No Financial Advice</h2>
            <p className="text-gray-400 leading-relaxed">
              Assagart is a portfolio management and tracking tool. Nothing on this platform constitutes financial, 
              investment, legal, or tax advice. The valuations, market data, insights, and other information provided 
              are for informational purposes only. Before making any investment decisions, you should consult with 
              qualified professionals including financial advisors, accountants, and legal counsel.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">2. AI Valuations</h2>
            <p className="text-gray-400 leading-relaxed">
              Our AI-powered valuation system provides estimates based on available market data, historical sales, 
              and algorithmic analysis. These valuations:
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li>Are <strong className="text-white">estimates only</strong> and may differ significantly from actual market values</li>
              <li>Should <strong className="text-white">not be used</strong> for insurance, sale, or legal purposes without professional verification</li>
              <li>May <strong className="text-white">not account for</strong> condition, provenance, authenticity, or other factors that affect value</li>
              <li>Are <strong className="text-white">subject to market volatility</strong> and may change rapidly</li>
            </ul>
            <div className="mt-4 p-4 bg-[#111111] border border-[#262626]">
              <p className="text-white text-sm font-medium">Recommendation:</p>
              <p className="text-gray-400 text-sm mt-1">
                Always obtain professional appraisals from certified valuers for insurance coverage, estate planning, 
                potential sales, or any legally binding purposes.
              </p>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">3. Market Data and Indices</h2>
            <p className="text-gray-400 leading-relaxed">
              Market indices and data displayed on the platform are compiled from various sources and may include 
              both real-time and simulated data for demonstration purposes. While we strive for accuracy, we cannot 
              guarantee that all market data is current, complete, or error-free. Market conditions can change rapidly, 
              and past performance is not indicative of future results.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">4. Third-Party Content</h2>
            <p className="text-gray-400 leading-relaxed">
              Assagart may display opportunities and listings from third-party sources including auction houses, 
              dealers, and brokers. We do not:
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li>Verify the authenticity or condition of items listed by third parties</li>
              <li>Guarantee the accuracy of third-party pricing or availability</li>
              <li>Act as a broker, dealer, or intermediary in any transactions</li>
              <li>Accept responsibility for any transactions conducted with third parties</li>
            </ul>
            <p className="text-gray-400 leading-relaxed mt-4">
              Users should conduct their own due diligence before engaging with any third-party seller or service.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">5. Investment Risks</h2>
            <p className="text-gray-400 leading-relaxed">
              Luxury assets and collectibles are alternative investments that carry significant risks:
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li><strong className="text-white">Market Risk:</strong> Values can decrease as well as increase</li>
              <li><strong className="text-white">Liquidity Risk:</strong> Luxury assets may be difficult to sell quickly</li>
              <li><strong className="text-white">Authentication Risk:</strong> Forgeries and misattributions exist in the market</li>
              <li><strong className="text-white">Storage and Insurance:</strong> Significant ongoing costs may apply</li>
              <li><strong className="text-white">Market Timing:</strong> The luxury market can be cyclical and unpredictable</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">6. No Warranties</h2>
            <p className="text-gray-400 leading-relaxed">
              The platform is provided "as is" and "as available" without any warranties of any kind, either express 
              or implied, including but not limited to implied warranties of merchantability, fitness for a particular 
              purpose, or non-infringement. We do not warrant that the platform will be uninterrupted, error-free, 
              or free of viruses or other harmful components.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">7. Limitation of Liability</h2>
            <p className="text-gray-400 leading-relaxed">
              To the fullest extent permitted by applicable law, Assagart and its affiliates, officers, directors, 
              employees, and agents shall not be liable for any direct, indirect, incidental, special, consequential, 
              or punitive damages arising from:
            </p>
            <ul className="text-gray-400 list-disc list-inside space-y-2 mt-4">
              <li>Your use of or inability to use the platform</li>
              <li>Any decisions made based on information provided on the platform</li>
              <li>Any transactions with third parties</li>
              <li>Errors, inaccuracies, or omissions in valuations or market data</li>
              <li>Unauthorised access to your account or data</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">8. Indemnification</h2>
            <p className="text-gray-400 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Assagart and its affiliates from any claims, damages, 
              losses, liabilities, costs, and expenses arising from your use of the platform or violation of this 
              Disclaimer or our Terms of Service.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">9. Jurisdictional Issues</h2>
            <p className="text-gray-400 leading-relaxed">
              Assagart is operated from the United Kingdom. We make no representation that the platform or its content 
              is appropriate or available for use in other locations. Those who access the platform from other locations 
              do so at their own risk and are responsible for compliance with local laws.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-white border-b border-[#262626] pb-2">10. Contact</h2>
            <p className="text-gray-400 leading-relaxed">
              If you have questions about this Disclaimer, please contact us:
            </p>
            <div className="mt-4 p-4 bg-[#111111] border border-[#262626]">
              <p className="text-white">Assagart Limited</p>
              <p className="text-gray-400">Email: assagart.contact@gmail.com</p>
              <p className="text-gray-400">Address: London, United Kingdom</p>
            </div>
          </section>

        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-[#1A1A1A] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-muted-foreground text-sm">© 2026 Assagart. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
